import React, { useContext, useEffect, useRef, useState } from "react";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import { Box, Tooltip, alpha } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink, useLocation } from "react-router-dom";

// --- New Modern Lucide Icons ---
import {
  LayoutDashboard,
  TableProperties,
  FileText,
  Map,
  Users,
  Database,
  Settings,
  ChevronUp,
  ChevronDown,
  ChevronLeft as ChevronLeftIcon, // Aliased to match your usage
  ChevronRight as ChevronRightIcon,
  Cog,
  Users2Icon,
  Link2,
  Code2,
  Badge, // Aliased to match your usage
  UserCog, // Added for TD Admin
} from "lucide-react";

// Assuming these are exported from your utilities/contexts
import { DRAWER_WIDTH, USER_ROLES } from "../utils/constants";
import { UserContext } from "../context/UserProvider";
import useAdminEventSettings from "../hooks/useAdminEventSettings";

const ICON_SIZE = 22;
const STROKE_WIDTH = 1.75;

// Standard menu items for other roles
const menuItems = [
  {
    text: "Dashboard",
    link: "/",
    icon: <LayoutDashboard size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "OES Forms",
    link: "/oes-forms",
    icon: <TableProperties size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    // Removed submenu, linking directly to the forms manager
    text: "Visitor Form",
    link: "/form-links",
    icon: <FileText size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "Floor Plan",
    link: "/floor-plan-creator",
    icon: <Map size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "Users List",
    icon: <Users2Icon size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    subcategories: [
      {
        text: "Exhibitor List",
        link: "/exhibitors-list",
        icon: <Users size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
      },
      {
        text: "Visitors List",
        link: "/visitors-list",
        icon: <Users size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
      },
      {
        text: "Trade delegates",
        link: "/tradedelegates-list",
        icon: <Users size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
      },
      {
        text: "Service Codes",
        link: "/service-codes-list",
        icon: <Code2 size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
      },
      {
        text: "Print Staff Badge",
        link: "/print-staff-badge",
        icon: <Badge size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
      },
    ],
  },
  {
    text: "Database",
    link: "/export-data",
    icon: <Database size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "Settings",
    link: "/settings",
    icon: <Settings size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "OES Explorer",
    link: "/oes-explorer",
    icon: <Cog size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    role: USER_ROLES.OES, // Restrict this item to the OES role
  },
  {
    text: "Onsite Module",
    link: "/onsite-links",
    icon: <Link2 size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
];

const SideMenuBar = ({ open, setOpen }) => {
  const { user, loading, reloadUser } = useContext(UserContext);
  const { data } = useAdminEventSettings();
  const theme = useTheme();
  const location = useLocation(); // Used to determine active route
  const [openSubmenu, setOpenSubmenu] = useState({});
  const drawerRef = useRef(null);

  // Filter menu items dynamically based on user role
  let visibleMenuItems = [];

  if (user?.role === USER_ROLES.TD_ADMIN) {
    // If user is TD_ADMIN, they only get this single menu item
    visibleMenuItems = [
      {
        text: "Manage TDs",
        link: "/manage-delegates",
        icon: <UserCog size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
      },
    ];
  } else {
    // For all other roles, filter the standard menu array by the 'role' property
    visibleMenuItems = menuItems.filter(
      (item) => !item.role || item.role === user?.role
    );
  }

  const handleClickSubmenu = (submenu) => {
    setOpenSubmenu((prevOpen) => ({
      ...prevOpen,
      [submenu]: !prevOpen[submenu],
    }));
    setOpen(true);
  };

  const renderMenuItems = (items, nestingLevel = 0) =>
    items.map((item) => {
      const isExternal = item.link?.startsWith("http");
      const isActive = location.pathname === item.link;
      const isSubmenuOpen = openSubmenu[item.text];

      // Handle Component routing dynamically
      let componentProps = {};
      if (item.link) {
        if (isExternal) {
          componentProps = {
            component: "a",
            href: item.link,
            target: "_blank",
            rel: "noopener noreferrer",
          };
        } else {
          componentProps = { component: RouterLink, to: item.link };
        }
      } else {
        componentProps = { component: "div" }; // Fallback for parent categories
      }

      const listItem = (
        <ListItemButton
          {...componentProps}
          onClick={() =>
            item.subcategories ? handleClickSubmenu(item.text) : null
          }
          sx={{
            justifyContent: open ? "initial" : "center",
            // Handle the active route background manually
            backgroundColor: isActive ? "rgba(2, 71, 95, 0.1)" : "transparent",
            color: isActive
              ? theme.palette.primary.main
              : theme.palette.text.primary,
            ...(nestingLevel > 0 && open && { pl: 4 }), // Indent nested items
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 2 : "auto",
              justifyContent: "center",
              color: isActive
                ? theme.palette.primary.main
                : theme.palette.action.active,
              transition: "margin 0.3s ease",
            }}
          >
            {item.icon}
          </ListItemIcon>

          <ListItemText
            primary={item.text}
            sx={{
              opacity: open ? 1 : 0,
              transition: "opacity 0.3s ease",
              "& .MuiTypography-root": {
                fontWeight: isActive ? 600 : 400,
                fontSize: "0.9rem",
              },
            }}
          />

          {item.subcategories &&
            open &&
            (isSubmenuOpen ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronDown size={18} />
            ))}
        </ListItemButton>
      );

      return (
        <React.Fragment key={item.text}>
          {!open ? (
            <Tooltip title={item.text} placement="right" arrow>
              {listItem}
            </Tooltip>
          ) : (
            listItem
          )}
          {item.subcategories && (
            <Collapse in={isSubmenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(item.subcategories, nestingLevel + 1)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });

  // Close drawer on outside click
  useEffect(() => {
    if (open) {
      const handleClickOutside = (event) => {
        if (drawerRef.current && !drawerRef.current.contains(event.target)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open, setOpen]);

  // Close submenus when drawer closes
  useEffect(() => {
    if (!open) setOpenSubmenu({});
  }, [open]);

  // Smooth drawer transition mixin
  const drawerStyles = {
    width: open ? DRAWER_WIDTH : theme.spacing(8),
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
  };

  return (
    <Drawer
      ref={drawerRef}
      variant="permanent"
      open={open}
      sx={{
        width: drawerStyles.width,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        "& .MuiDrawer-paper": {
          ...drawerStyles,
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          sx={{
            height: "64px",
            display: "flex",
            alignItems: "center",
            padding: "0 8px",
            justifyContent: open ? "space-between" : "center",
            "@media (max-width: 600px)": { height: "56px" },
          }}
        >
          {open && data?.eventLogo && (
            <Box
              component="img"
              sx={{
                ml: 1,
                maxWidth: "140px",
                maxHeight: "40px",
                objectFit: "contain",
              }}
              src={data.eventLogo}
              alt="Logo"
            />
          )}
          <IconButton
            onClick={() => setOpen(!open)}
            sx={{
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {open ? (
              <ChevronLeftIcon size={20} />
            ) : (
              <ChevronRightIcon size={20} />
            )}
          </IconButton>
        </Box>
        <Divider sx={{ mb: 1 }} />

        {/* Custom scrollbar styling for a cleaner look */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.mode === "dark" ? "#555" : "#ccc",
              borderRadius: "4px",
            },
            "&:hover::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.mode === "dark" ? "#777" : "#aaa",
            },
          }}
        >
          {/* Use the filtered visibleMenuItems here */}
          <List sx={{ pt: 0 }}>{renderMenuItems(visibleMenuItems)}</List>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SideMenuBar;
